import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  Pencil,
  Trash2,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { rbacService } from "../../../services/rbac.service";
import { permissionLabel } from "./PermissionsDrawer";

const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";

function ChipButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-[38px] rounded-full bg-[#d86a78] px-6 text-[13px] font-semibold text-white hover:bg-[#c95b69] active:scale-[0.99] transition"
    >
      {children}
    </button>
  );
}

function SelectLike({ value, onChange, placeholder, options = [] }) {
  return (
    <div className="relative h-[38px]">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-[34px] w-full appearance-none rounded-[8px] border border-[#f2c6cc] bg-white px-4 pr-9 text-[11px] text-slate-500 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const label = typeof o === "string" ? o : o.label;
          return (
            <option key={val} value={val}>
              {label}
            </option>
          );
        })}
      </select>

      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
    </div>
  );
}

function formatAppliesTo(value) {
  const map = {
    admin_employee: "Admin Panel",
    warehouse_employee: "Warehouse Panel",
    both: "Both Panels",
  };
  return map[String(value || "")] || "-";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export default function ManageRoles() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [appliesTo, setAppliesTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popup, setPopup] = useState({ open: false, row: null });

  const [permissionPopup, setPermissionPopup] = useState({
  open: false,
  permissions: [],
  roleName: "",
});

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await rbacService.listRoles();
        if (!mounted) return;
        setList(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setList([]);
        setError(e?.message || "Failed to load roles");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const roleOptions = useMemo(() => {
    const uniq = new Set((list || []).map((r) => String(r.name || "").trim()).filter(Boolean));
    return Array.from(uniq).map((name) => ({ value: name, label: name }));
  }, [list]);

  const appliesToOptions = useMemo(() => {
    const uniq = new Set((list || []).map((r) => String(r.appliesTo || "").trim()).filter(Boolean));
    return Array.from(uniq).map((value) => ({ value, label: formatAppliesTo(value) }));
  }, [list]);

  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();

    const filtered = (list || []).filter((r) => {
      const name = String(r.name || "");
      const description = String(r.description || "");
      const target = String(r.appliesTo || "");

      if (qq) {
        const hay = `${name} ${description} ${target}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }

      if (role && name !== role) return false;
      if (appliesTo && target !== appliesTo) return false;
      return true;
    });

    const start = (page - 1) * pageSize;
    return {
      total: filtered.length,
      pageCount: Math.max(1, Math.ceil(filtered.length / pageSize)),
      items: filtered.slice(start, start + pageSize),
    };
  }, [appliesTo, list, page, q, role]);

  useEffect(() => {
    if (page > rows.pageCount) setPage(rows.pageCount);
  }, [page, rows.pageCount]);

  return (
    <div className="w-full">
      <ConfirmModal
        open={popup.open}
        icon="trash"
        variant="confirm"
        title={`Are you sure you want to delete ${popup.row?.name || "this role"}?`}
        description="This will remove the role from the system. Employees mapped to this role may lose access until a new role is assigned."
        onClose={() => setPopup({ open: false, row: null })}
        onConfirm={async () => {
          const id = popup.row?.id;
          try {
            if (id) await rbacService.deleteRole(id);
            setList((prev) => prev.filter((item) => item.id !== id));
            setPopup({ open: false, row: null });
          } catch (e) {
            alert(e?.message || "Delete failed");
          }
        }}
        cancelText="NO"
        confirmText="YES"
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
  <button
    type="button"
    onClick={() => navigate("/admin/dashboard/employees")}
    className="mt-1 inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#f2c6cc] bg-white text-[#d86a78] transition hover:bg-[#fff1f3]"
    title="Back"
  >
    <ArrowLeft size={17} />
  </button>

  <div>
    <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
      MANAGE ROLES
    </h1>
    <p className="mt-1 text-[12px] text-slate-500">
      View all roles, filter by panel type, and create new roles with mapped permissions.
    </p>
  </div>
</div>

        <div className="flex flex-wrap items-center gap-3">
          <ChipButton onClick={() => navigate("/admin/dashboard/roles/new")}>Add New Role</ChipButton>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-[220px]">
            <input
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search roles"
              className="h-[34px] w-full rounded-[8px] border border-[#f2c6cc] bg-white pl-9 pr-4 text-[11px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300"
            />
          </div>

          <div className="grid w-full grid-cols-1 gap-3 sm:w-[360px] sm:grid-cols-2">
            <SelectLike
              value={role}
              onChange={(v) => {
                setPage(1);
                setRole(v);
              }}
              placeholder="All Roles"
              options={roleOptions}
            />

            <SelectLike
              value={appliesTo}
              onChange={(v) => {
                setPage(1);
                setAppliesTo(v);
              }}
              placeholder="All Panels"
              options={appliesToOptions}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 lg:ml-auto">
          <div className="inline-flex h-[30px] items-center rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[13px] font-semibold text-[#d86a78]">
            {rows.total} Total Roles
          </div>
          <button
            type="button"
            onClick={() => {
              setQ("");
              setRole("");
              setAppliesTo("");
              setPage(1);
            }}
            className="inline-flex h-[30px] items-center rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-slate-500 transition hover:text-[#d86a78]"
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-[14px] border border-slate-200/70 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-[40px] border-b border-slate-200/70 px-4 py-3" />
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  Role
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  Applies To
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[15px] font-bold text-[#d86a78]">
                  Enrolled Employees
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[15px] font-bold text-[#d86a78]">
                  Created On
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-left text-[15px] font-bold text-[#d86a78]">
                  Permissions
                </th>
                <th className="border-b border-slate-200/70 px-4 py-3 text-center text-[15px] font-bold text-[#d86a78]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="border-b border-slate-200/70 px-6 py-10 text-center text-[13px] text-slate-400">
                    Loading roles...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="border-b border-slate-200/70 px-6 py-10 text-center text-[13px] text-red-500">
                    {error}
                  </td>
                </tr>
              ) : rows.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="border-b border-slate-200/70 px-6 py-10 text-center text-[13px] text-slate-400">
                    No roles found.
                  </td>
                </tr>
              ) : (
                rows.items.map((r) => {
                  const permissions = Array.isArray(r.permissions) ? r.permissions : [];
                  return (
                    <tr key={r.id} className="hover:bg-slate-50/60">
                      <td className="border-b border-slate-200/70 px-4 py-4 align-top">
                        <input type="checkbox" className="h-[14px] w-[14px] rounded border-slate-300" />
                      </td>

                      <td className="border-b border-slate-200/70 px-4 py-4 align-top">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 rounded-full bg-[#fff1f3] p-2 text-[#d86a78]">
                            <ShieldCheck size={16} />
                          </div>
                          <div>
                            <div className="text-[14px] font-semibold text-slate-700">{r.name || "-"}</div>
                            <div className="mt-1 max-w-[240px] text-[12px] text-slate-500">
                              {r.description || "No description added."}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="border-b border-slate-200/70 px-4 py-4 align-top">
                        <span className="inline-flex rounded-full border border-[#f2c6cc] bg-[#fff7f8] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
                          {formatAppliesTo(r.appliesTo)}
                        </span>
                      </td>

                      <td className="border-b border-slate-200/70 px-4 py-4 align-top text-center">
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-[12px] font-semibold text-slate-600">
                          <Users size={14} />
                          {Number(r.enrolledEmployees || 0)}
                        </div>
                      </td>

                      <td className="border-b border-slate-200/70 px-4 py-4 align-top text-center text-[13px] text-slate-600">
                        {formatDate(r.createdAt)}
                      </td>

                      <td className="border-b border-slate-200/70 px-4 py-4 align-top">
                        {permissions.length ? (
                          <div className="flex max-w-[320px] flex-wrap gap-2">
                            {permissions.slice(0, 4).map((key) => (
                              <span
                                key={key}
                                className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600"
                                title={key}
                              >
                                {permissionLabel(key)}
                              </span>
                            ))}
                          {permissions.length > 4 ? (
  <button
    onClick={() =>
      setPermissionPopup({
        open: true,
        permissions,
        roleName: r.name,
      })
    }
    className="rounded-full border border-[#f2c6cc] bg-[#fff7f8] px-2.5 py-1 text-[11px] font-semibold text-[#d86a78] hover:bg-[#fdecee]"
  >
    +{permissions.length - 4} more
  </button>
) : null}
                          </div>
                        ) : (
                          <div className="text-[12px] text-slate-400">No permissions mapped</div>
                        )}
                      </td>

                      <td className="border-b border-slate-200/70 px-4 py-4 align-top">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/dashboard/roles/${r.id}/edit`)}
                            className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-[8px] border border-slate-200 text-slate-500 transition hover:border-[#f2c6cc] hover:bg-[#fff7f8] hover:text-[#d86a78]"
                            title="Edit Role"
                          >
                            <Pencil size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => setPopup({ open: true, row: r })}
                            className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-[8px] border border-slate-200 text-slate-500 transition hover:border-[#f2c6cc] hover:bg-[#fff7f8] hover:text-[#d86a78]"
                            title="Delete Role"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {permissionPopup.open && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="w-[500px] max-h-[400px] overflow-auto rounded-[12px] bg-white p-5 shadow-lg">
      
      <h2 className="text-[16px] font-bold text-[#d86a78] mb-3">
        {permissionPopup.roleName} - All Permissions
      </h2>

      <div className="flex flex-wrap gap-2">
        {permissionPopup.permissions.map((p) => (
          <span
            key={p}
            title={p}
            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[12px] text-slate-600"
          >
            {permissionLabel(p)}
          </span>
        ))}
      </div>

      <div className="mt-4 text-right">
        <button
          onClick={() =>
            setPermissionPopup({ open: false, permissions: [], roleName: "" })
          }
          className="rounded-[8px] bg-[#d86a78] px-4 py-2 text-white text-[12px] font-semibold hover:bg-[#c95b69]"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-[12px] text-slate-500">
          Showing <span className="font-semibold text-slate-700">{rows.items.length}</span> of{' '}
          <span className="font-semibold text-slate-700">{rows.total}</span> roles
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-[34px] rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-50 hover:border-[#d86a78] hover:text-[#d86a78]"
          >
            Previous
          </button>

          <div className="inline-flex h-[34px] items-center rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78]">
            Page {page} / {rows.pageCount}
          </div>

          <button
            type="button"
            disabled={page >= rows.pageCount}
            onClick={() => setPage((p) => Math.min(rows.pageCount, p + 1))}
            className="h-[34px] rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-50 hover:border-[#d86a78] hover:text-[#d86a78]"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
